import { getBudgetAmountInRange, hasBudgetAmountChangeBy } from './budgetAmounts';
/**
 * The Analysis page's forecast math as pure functions, shared between
 * BudgetView (the "Account Balances" table) and the Accounts page cards so a
 * card's dashed month-end forecast always equals the same month's cell in the
 * Analysis table:
 *
 * - Budgets land as whole-month amounts. A budget tied to an account lands
 *   fully on that account; an "All Accounts" budget is split evenly across
 *   every active account.
 * - Actions land monthly for the months their schedule covers, with weekly
 *   actions multiplied by that month's weekday count.
 * - Each forecast month's ending balance chains off the previous month's.
 */

import { monthKeysAhead } from './entityTrendSeries';
import { categoryAccountShares } from './categoryAccountUtils';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MS_PER_DAY = 1000 * 60 * 60 * 24;

const round2 = (n) => Math.round(n * 100) / 100;

/** 'Aug-26' -> that month's first/last day plus its parts. */
const parseMonth = (monthStr) => {
  const [monthAbbr, yearStr] = monthStr.split('-');
  const monthIndex = MONTH_NAMES.indexOf(monthAbbr);
  const year = 2000 + parseInt(yearStr, 10);
  return {
    year,
    monthIndex,
    startOfMonth: new Date(year, monthIndex, 1),
    endOfMonth: new Date(year, monthIndex + 1, 0),
  };
};

/** How much of one budget lands in one calendar month. */
const budgetAmountInMonth = (budget, startOfMonth, endOfMonth) => {
  if (hasBudgetAmountChangeBy(budget, endOfMonth)) return getBudgetAmountInRange(budget, startOfMonth, endOfMonth);
  const budgetStart = new Date(budget.startDate);
  const budgetEnd = budget.endDate ? new Date(budget.endDate) : new Date(2099, 11, 31);
  if (budgetStart > endOfMonth || budgetEnd < startOfMonth) return 0;

  const daysInMonth = (endOfMonth - startOfMonth) / MS_PER_DAY + 1;
  const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
  const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
  const daysOverlap = (overlapEnd - overlapStart) / MS_PER_DAY + 1;

  if (!budget.isRecurring) {
    return Math.abs(budget.amount) * (daysOverlap / daysInMonth);
  }

  if (budget.isProrated) {
    switch (budget.recurringPeriod) {
      case 'weekly':
        return (budget.amount / 7) * daysOverlap;
      case 'monthly':
        return budget.amount * (daysOverlap / daysInMonth);
      case 'quarterly':
        return budget.amount * (daysOverlap / 91.25);
      case 'yearly':
        return budget.amount * (daysOverlap / 365);
      default:
        return budget.amount * (daysOverlap / 30.4375);
    }
  }

  switch (budget.recurringPeriod) {
    case 'monthly':
      return budget.amount;
    case 'quarterly': {
      // Full amount only in occurrence months (every 3 months from start)
      const diff =
        (startOfMonth.getFullYear() - budgetStart.getFullYear()) * 12 +
        (startOfMonth.getMonth() - budgetStart.getMonth());
      return diff >= 0 && diff % 3 === 0 ? budget.amount : 0;
    }
    case 'yearly':
      return budget.amount / 12;
    case 'weekly': {
      // Count matching weekdays in the month
      const targetDow = budgetStart.getDay();
      let count = 0;
      for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
        if (d >= budgetStart && d <= budgetEnd && d.getDay() === targetDow) count++;
      }
      return count * budget.amount;
    }
    default:
      return budget.amount;
  }
};

/**
 * Per-category budgeted amounts for each forecast month, plus a monthly
 * income/expense summary — the Analysis page's forecastData. Only categories
 * that appear in budgetData (i.e. have actual transactions) get a series;
 * 'uncategorized' forecasts as the average of the last three actual months.
 *
 * Returns { incomeCategories, expenseCategories, monthlySummary } keyed the
 * same way as useBudgetData's maps.
 */
export const computeForecastCategoryAmounts = ({ forecastMonths, budgets, categories, budgetData }) => {
  const incomeCategories = {};
  const expenseCategories = {};
  const monthlySummary = {};

  Object.keys(budgetData.incomeCategories).forEach((categoryId) => {
    incomeCategories[categoryId] = {};
    forecastMonths.forEach((month) => { incomeCategories[categoryId][month] = 0; });
  });
  Object.keys(budgetData.expenseCategories).forEach((categoryId) => {
    expenseCategories[categoryId] = {};
    forecastMonths.forEach((month) => { expenseCategories[categoryId][month] = 0; });
  });
  forecastMonths.forEach((month) => {
    monthlySummary[month] = { income: 0, expenses: 0 };
  });

  forecastMonths.forEach((monthStr) => {
    const { startOfMonth, endOfMonth } = parseMonth(monthStr);

    ['Income', 'Expense'].forEach((type) => {
      const target = type === 'Income' ? incomeCategories : expenseCategories;
      categories.filter((c) => c.type === type).forEach((category) => {
        const totalBudget = budgets
          .filter((b) => b.categoryId === category.id && b.type === type && b.isActive)
          .reduce((sum, b) => sum + budgetAmountInMonth(b, startOfMonth, endOfMonth), 0);

        if (target[category.id]) {
          target[category.id][monthStr] = totalBudget;
          if (type === 'Income') monthlySummary[monthStr].income += totalBudget;
          else monthlySummary[monthStr].expenses += totalBudget;
        }
      });
    });

    if (budgetData.months.length >= 3) {
      const last3Months = budgetData.months.slice(-3);
      if (incomeCategories['uncategorized']) {
        const avgIncome =
          last3Months.reduce((s, m) => s + (budgetData.incomeCategories['uncategorized']?.[m] || 0), 0) / 3;
        incomeCategories['uncategorized'][monthStr] = avgIncome;
        monthlySummary[monthStr].income += avgIncome;
      }
      if (expenseCategories['uncategorized']) {
        const avgExpense =
          last3Months.reduce((s, m) => s + (budgetData.expenseCategories['uncategorized']?.[m] || 0), 0) / 3;
        expenseCategories['uncategorized'][monthStr] = avgExpense;
        monthlySummary[monthStr].expenses += avgExpense;
      }
    }
  });

  return { incomeCategories, expenseCategories, monthlySummary };
};

/** Occurrences of a weekly action's weekday inside one calendar month. */
const countWeeklyOccurrencesInMonth = (startDate, year, monthIndex) => {
  const startDay = new Date(startDate).getDay();
  const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
  const date = new Date(year, monthIndex, 1);
  while (date.getDay() !== startDay) date.setDate(date.getDate() + 1);
  let count = 0;
  while (date <= lastDayOfMonth) {
    count++;
    date.setDate(date.getDate() + 7);
  }
  return count;
};

/**
 * One account's forecast-month movements: budgeted category amounts routed to
 * the account (whole when the budget names it, an even split across active
 * accounts when it's an "All Accounts" budget) plus scheduled action amounts.
 * `forecastAmounts` is a computeForecastCategoryAmounts() result.
 *
 * Returns { budgetMovements, totalBudgetMovement, actionMovements,
 * totalActionMovement } in the shapes the Analysis detail dialog renders.
 */
export const computeForecastMovements = ({
  accountId,
  month,
  accounts,
  budgets,
  categories,
  actions,
  forecastAmounts,
  transactions = [],
}) => {
  const account = accounts.find((a) => a.id === accountId);
  const { year, monthIndex, startOfMonth, endOfMonth } = parseMonth(month);

  // ------------------------------------------------------ budget movements
  const budgetMovements = [];
  let totalBudgetMovement = 0;
  const activeAccountIds = accounts.filter((a) => a.isActive).map((a) => a.id);
  const activeAccountCount = activeAccountIds.length || 1;

  ['Income', 'Expense'].forEach((type) => {
    const amounts = type === 'Income' ? forecastAmounts.incomeCategories : forecastAmounts.expenseCategories;
    const sign = type === 'Income' ? 1 : -1;
    categories.filter((c) => c.type === type).forEach((category) => {
      const budgetAmount = amounts[category.id]?.[month] || 0;
      if (budgetAmount <= 0) return;

      const categoryBudgets = budgets.filter(
        (b) => b.categoryId === category.id && b.type === type && b.isActive
      );
      const hasExplicitAccount = categoryBudgets.some((b) => b.accountId === accountId);
      const hasAnyAllAccountsBudgets = categoryBudgets.some((b) => !b.accountId);

      const label = type === 'Income' ? 'income' : 'expense';
      if (hasExplicitAccount) {
        budgetMovements.push({
          type: label,
          category: category.name || category.id,
          description: `Budgeted ${label} for ${category.name || category.id}`,
          amount: sign * budgetAmount,
        });
        totalBudgetMovement += sign * budgetAmount;
      } else if (hasAnyAllAccountsBudgets) {
        // An "All Accounts" budget lands where the category's money actually
        // moves. Only when there is no history to learn from does it fall back
        // to an even split — which otherwise charges every account a slice of
        // spending it could never incur.
        const shares = categoryAccountShares({
          categoryId: category.id,
          type,
          transactions,
          accountIds: activeAccountIds,
        });
        const share = shares ? (shares[accountId] || 0) : 1 / activeAccountCount;
        if (!share) return;
        budgetMovements.push({
          type: label,
          category: category.name || category.id,
          description: `Budgeted ${label} for ${category.name || category.id} (${shares ? 'by past spend' : 'split'})`,
          amount: sign * budgetAmount * share,
        });
        totalBudgetMovement += sign * budgetAmount * share;
      }
    });
  });

  // ------------------------------------------------------ action movements
  const actionMovements = [];
  let totalActionMovement = 0;

  const relevantActions = actions.filter((action) => {
    if (!action.isActive || action.isCompleted) return false;
    if (action.sourceAccountId !== accountId && action.destinationAccountId !== accountId) return false;

    const actionDate = new Date(action.startDate);
    if (action.frequency === 'once') {
      return actionDate >= startOfMonth && actionDate <= endOfMonth;
    }
    // Recurring: started before month end and not ended before month start
    const actionEnd = action.endDate ? new Date(action.endDate) : null;
    return actionDate <= endOfMonth && (!actionEnd || actionEnd >= startOfMonth);
  });

  relevantActions.forEach((action) => {
    let amount = action.amount || 0;
    if (action.frequency === 'weekly') {
      amount *= countWeeklyOccurrencesInMonth(action.startDate, year, monthIndex);
    }
    const date = new Date(action.startDate).toLocaleDateString();

    const push = (name, type, sourceAccount, destAccount, signedAmount) => {
      actionMovements.push({
        date,
        name,
        type,
        sourceAccount,
        destAccount,
        amount: signedAmount,
        frequency: action.frequency,
      });
      totalActionMovement += signedAmount;
    };

    if (action.type === 'income') {
      const receives =
        action.destinationAccountId === accountId ||
        (!action.destinationAccountId && accountId === action.sourceAccountId) ||
        (!action.destinationAccountId && !action.sourceAccountId && account?.isPrimary);
      if (receives) push(action.name || 'Income', 'income', 'External', account?.name, amount);
    } else if (action.type === 'expense') {
      const pays =
        action.sourceAccountId === accountId ||
        (!action.sourceAccountId && action.destinationAccountId === accountId) ||
        (!action.sourceAccountId && !action.destinationAccountId && account?.isPrimary);
      if (pays) push(action.name || 'Expense', 'expense', account?.name, 'External', -amount);
    } else if (action.type === 'transfer') {
      const sourceAccount = accounts.find((a) => a.id === action.sourceAccountId);
      const destAccount = accounts.find((a) => a.id === action.destinationAccountId);
      if (action.sourceAccountId === accountId) {
        push(action.name || 'Transfer Out', 'transfer', account?.name, destAccount ? destAccount.name : 'Unknown Account', -amount);
      }
      if (action.destinationAccountId === accountId) {
        push(action.name || 'Transfer In', 'transfer', sourceAccount ? sourceAccount.name : 'Unknown Account', account?.name, amount);
      }
    }
  });

  return { budgetMovements, totalBudgetMovement, actionMovements, totalActionMovement };
};

/**
 * Month-end forecast series for every active account over the next
 * `monthsAhead` months — the Accounts page cards' dashed line. Each account
 * chains from its live balance through the same monthly movements the
 * Analysis table applies, so both surfaces show the same figures.
 *
 * Returns { [accountId]: [{ month, value }] }.
 */
export const forecastAccountBalanceSeries = ({
  accounts,
  budgets,
  categories,
  actions,
  budgetData,
  monthsAhead,
  transactions = [],
  today = new Date(),
}) => {
  const monthKeys = monthKeysAhead(monthsAhead, today);
  const forecastAmounts = computeForecastCategoryAmounts({
    forecastMonths: monthKeys.map((k) => k.key),
    budgets,
    categories,
    budgetData,
  });

  const series = {};
  accounts
    .filter((a) => a && a.id && a.isActive !== false)
    .forEach((account) => {
      let balance = Number(account.balance) || 0;
      series[account.id] = monthKeys.map(({ key }) => {
        const { totalBudgetMovement, totalActionMovement } = computeForecastMovements({
          accountId: account.id,
          month: key,
          accounts,
          budgets,
          categories,
          actions,
          forecastAmounts,
          transactions,
        });
        balance += totalBudgetMovement + totalActionMovement;
        return { month: key, value: round2(balance) };
      });
    });
  return series;
};

